import type {ChangeSpec} from '@codemirror/state'
import {ChangeSet, EditorSelection} from '@codemirror/state'
import type {Command} from '@codemirror/view'

import {buildTasklistPrefixSyntax} from '../../../language-tools/markdown/list-utils'
import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {isPositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'
import {createText} from '../../extensions/single-line-editor'

const DEFAULT_TASKLIST_PREFIX = buildTasklistPrefixSyntax(false)
const TASK_LIST_MARKER = `- ${DEFAULT_TASKLIST_PREFIX}`

export const formatTaskList: Command = view => {
  if (!view.hasFocus) view.focus()

  const selection = view.state.selection.main
  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()

  const from = selection.from
  const to = selection.to

  const lines = MarkdownLine.linesAtRange(tree, source, from, Math.max(to - 1, from))

  const changes: ChangeSpec[] = []

  for (const currentLine of lines) {
    const taskListPrefix = currentLine.prefixes.find(prefix => prefix.tokenType === 'taskListCheck')
    if (taskListPrefix && isPositionedNode(taskListPrefix)) {
      const removeFrom = taskListPrefix.position.start.offset
      let removeTo = taskListPrefix.position.end.offset
      if (source.charAt(removeTo) === ' ') removeTo += 1
      changes.push({from: removeFrom, to: removeTo})
      continue
    }

    const listItemPrefix = currentLine.prefixes.findLast(prefix => prefix.tokenType === 'listItemPrefix')
    if (listItemPrefix && isPositionedNode(listItemPrefix)) {
      const insertPosition = listItemPrefix.position.end.offset
      const existingTaskMarker = source.slice(insertPosition, currentLine.end)
      const existingTaskMatch = existingTaskMarker.match(/^\[[ xX]\]\s*/)
      if (existingTaskMatch) {
        changes.push({from: insertPosition, to: insertPosition + existingTaskMatch[0].length})
        continue
      }
      changes.push({from: insertPosition, to: insertPosition, insert: createText(DEFAULT_TASKLIST_PREFIX)})
      continue
    }

    const insertPosition = currentLine.contentStart
    const existing = view.state.doc.sliceString(insertPosition, insertPosition + TASK_LIST_MARKER.length)
    if (existing === TASK_LIST_MARKER) continue
    changes.push({from: insertPosition, to: insertPosition, insert: createText(TASK_LIST_MARKER)})
  }

  if (changes.length === 0) return true

  const changeSet = ChangeSet.of(changes, view.state.doc.length)
  const selectionIsCollapsed = selection.anchor === selection.head
  const selectionForward = selection.anchor <= selection.head

  const anchorAssoc = selectionIsCollapsed ? 1 : selectionForward ? -1 : 1
  const headAssoc = selectionIsCollapsed ? 1 : selectionForward ? 1 : -1

  const anchor = changeSet.mapPos(selection.anchor, anchorAssoc)
  const head = changeSet.mapPos(selection.head, headAssoc)

  view.dispatch({
    changes,
    selection: EditorSelection.single(anchor, head),
    userEvent: 'input',
    scrollIntoView: true,
  })

  return true
}
